import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  getPaymentMethodsForCountry,
  TEMPORARILY_UNAVAILABLE_COUNTRIES,
} from '@/constants/paymentMethods'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { useDynamicSupportedRoutes } from '@/hooks/useCpn'
import {
  COUNTRY_DISPLAY_PRIORITY,
  CpnCountryCode,
  DynamicDestinationOption,
  getDynamicDestinationOptions,
} from '@/utils/cpn'

const isCountryUnavailable = (countryCode: CpnCountryCode) =>
  TEMPORARILY_UNAVAILABLE_COUNTRIES.includes(countryCode)

interface CurrencySelectorProps {
  onSelect: (
    currency: string,
    flag: string,
    countryCode: string,
    paymentMethod?: string
  ) => void
  onClose: () => void
}

const CurrencySelector = ({ onSelect, onClose }: CurrencySelectorProps) => {
  const { data: dynamicRoutes, isLoading, error } = useDynamicSupportedRoutes()
  const { isTesting } = useFeatureFlags()

  // Filter and transform destination options based on allowed payment methods
  const getFilteredDestinationOptions = () => {
    if (!dynamicRoutes) return []

    const allOptions = getDynamicDestinationOptions(dynamicRoutes)

    // Only show local currencies with at least one allowed payment method (or temporarily unavailable)
    return allOptions
      .filter((option) => option.isLocal)
      .map((option) => {
        const allowedMethods = getPaymentMethodsForCountry(
          option.countryCode,
          isTesting
        )
        const filteredMethods = option.paymentMethods.filter((method) =>
          allowedMethods.includes(method)
        )

        return {
          ...option,
          paymentMethods: filteredMethods,
          defaultPaymentMethod:
            filteredMethods[0] || option.defaultPaymentMethod,
        }
      })
      .filter(
        (option) =>
          option.paymentMethods.length > 0 ||
          isCountryUnavailable(option.countryCode as CpnCountryCode)
      )
      .sort((a, b) => {
        const priorityA = COUNTRY_DISPLAY_PRIORITY.indexOf(
          a.countryCode as CpnCountryCode
        )
        const priorityB = COUNTRY_DISPLAY_PRIORITY.indexOf(
          b.countryCode as CpnCountryCode
        )
        if (priorityA !== -1 && priorityB !== -1) return priorityA - priorityB
        if (priorityA !== -1) return -1
        if (priorityB !== -1) return 1
        return a.name.localeCompare(b.name)
      })
  }
  const destinationOptions = getFilteredDestinationOptions()

  const handleCurrencyClick = (option: DynamicDestinationOption) => {
    // Use dynamic data from API instead of static helper functions
    const paymentMethods = option.paymentMethods

    const selectedPaymentMethod =
      paymentMethods.length > 0
        ? option.defaultPaymentMethod
        : paymentMethods[0]
    onSelect(
      option.currency,
      option.flag,
      option.countryCode,
      selectedPaymentMethod
    )
  }

  return (
    <div
      className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-md bg-white dark:bg-gray-800 rounded-3xl p-6 animate-in fade-in-0 zoom-in-95 duration-300 mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Select Currency
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="space-y-2 max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500 dark:text-gray-400">
                Loading currencies...
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-red-500">
                Error loading currencies. Please try again.
              </div>
            </div>
          ) : destinationOptions.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500 dark:text-gray-400">
                No currencies available
              </div>
            </div>
          ) : (
            destinationOptions.map((option) => {
              const unavailable = isCountryUnavailable(
                option.countryCode as CpnCountryCode
              )

              return (
                <button
                  key={`${option.countryCode}-${option.currency}`}
                  onClick={() => handleCurrencyClick(option)}
                  disabled={unavailable}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                    unavailable
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="text-2xl">{option.flag}</span>
                  <div className="text-left flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {option.currency}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({option.countryCode})
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {option.name}
                    </div>
                    {unavailable ? (
                      <div className="text-xs text-amber-600 dark:text-amber-400">
                        Temporarily unavailable
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 dark:text-gray-500">
                        {option.paymentMethods.slice(0, 2).join(', ')}
                        {option.paymentMethods.length > 2
                          ? `, +${option.paymentMethods.length - 2} more`
                          : ''}
                      </div>
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>
      </Card>
    </div>
  )
}

export default CurrencySelector
